import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articleContents, featuredArticles, categoryArticles, siteConfig } from '@/mocks/site';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const articleId = id ? parseInt(id, 10) : 0;
  const article = articleContents[articleId];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 pb-20 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-600">文章未找到</p>
            <a href="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categorySlug = article.categorySlug;
  const relatedArticles = categorySlug
    ? (categoryArticles[categorySlug] || [])
        .filter((a) => a.id !== articleId)
        .slice(0, 3)
    : featuredArticles.filter((a) => a.id !== articleId).slice(0, 3);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    description: article.body.substring(0, 160),
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '分类浏览', item: `${siteConfig.url}/categories` },
      { '@type': 'ListItem', position: 3, name: article.category, item: `${siteConfig.url}/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 4, name: article.title },
    ],
  };

  const htmlContent = article.body
    .split('\n\n')
    .map((block) => {
      if (block.startsWith('## ')) {
        return `<h2 class="text-xl font-semibold text-foreground-950 mt-8 mb-4">${block.slice(3)}</h2>`;
      }
      if (block.startsWith('### ')) {
        return `<h3 class="text-lg font-semibold text-foreground-900 mt-6 mb-3">${block.slice(4)}</h3>`;
      }
      if (block.startsWith('- ')) {
        const items = block.split('\n').map((item) => item.replace('- ', ''));
        return `<ul class="list-disc pl-5 space-y-1 text-foreground-700 my-3">${items.map((item) => `<li>${item}</li>`).join('')}</ul>`;
      }
      if (block.startsWith('1. ') || block.startsWith('2. ') || block.startsWith('3. ') || block.startsWith('4. ') || block.startsWith('5. ')) {
        const items = block.split('\n').map((item) => item.replace(/^\d+\.\s/, ''));
        return `<ol class="list-decimal pl-5 space-y-1 text-foreground-700 my-3">${items.map((item) => `<li>${item}</li>`).join('')}</ol>`;
      }
      return `<p class="text-foreground-700 leading-relaxed mb-4">${block}</p>`;
    })
    .join('');

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-sm mb-6 flex-wrap" aria-label="面包屑导航">
              <a href="/" className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">首页</a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <a href="/categories" className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">分类浏览</a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <a href={`/category/${article.categorySlug}`} className="text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">{article.category}</a>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">{article.title}</span>
            </nav>

            <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 撸撸社${article.category}`}
              />
            </div>

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <a
                  href={`/category/${article.categorySlug}`}
                  className="text-xs font-medium text-primary-500 bg-primary-50 px-2.5 py-0.5 rounded whitespace-nowrap hover:bg-primary-100 transition-colors"
                >
                  {article.category}
                </a>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="font-medium text-foreground-800 whitespace-nowrap">{article.author}</span>
                </div>
                <span className="whitespace-nowrap">{article.date}</span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime} 分钟阅读
                </span>
                <span className="whitespace-nowrap flex items-center gap-1">
                  <i className="ri-heart-line"></i> {article.likes >= 1000 ? `${(article.likes / 1000).toFixed(1)}k` : article.likes}
                </span>
              </div>
            </header>

            <div
              className="prose-custom text-base leading-relaxed"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs font-medium text-secondary-900 bg-secondary-100 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-heart-line text-lg"></i> 点赞 ({article.likes >= 1000 ? `${(article.likes / 1000).toFixed(1)}k` : article.likes})
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line text-lg"></i> 收藏
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-share-forward-line text-lg"></i> 分享
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
              <h2 className="text-xl font-heading text-foreground-950 mb-6">相关文章推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    id={ra.id}
                    title={ra.title}
                    excerpt={ra.excerpt}
                    category={article.category}
                    categorySlug={article.categorySlug}
                    author={ra.author}
                    authorAvatar={ra.authorAvatar}
                    readTime={ra.readTime}
                    likes={ra.likes}
                    date={ra.date}
                    imageUrl={ra.imageUrl}
                    isHot={ra.isHot}
                  />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}